import React, { useState, useMemo } from "react";
import axiosInstance from "../components/api/axiosInstance";
import TrainingLeadsTable from "./TrainingLeadsTable";
import "../assets/css/DigixLeads.css";

/**
 * Shared API helper for searching training leads.
 * Used by both this page component and the DigixLeads container.
 */
export async function searchTrainingLeadsApi(filters) {
  const params = new URLSearchParams();

  const nameOrPhoneValue =
    (filters.nameOrPhone || filters.phone || "").trim();
  if (nameOrPhoneValue) {
    const digitsOnly = nameOrPhoneValue.replace(/\D/g, "");
    const hasDigits = digitsOnly.length > 0;
    if (hasDigits) {
      params.append("phone", nameOrPhoneValue);
    } else {
      params.append("name", nameOrPhoneValue);
    }
  }
  if (filters.status && filters.status !== "All Statuses") {
    params.append("status", filters.status);
  }
  if (filters.nextFollowupDate) {
    const dateValue = filters.nextFollowupDate.trim();
    if (dateValue) {
      params.append("next_followup_date", dateValue);
    }
  }
  if (filters.courseId) params.append("course_id", filters.courseId);
  if (filters.mode) params.append("mode", filters.mode);
  if (filters.batch) params.append("batch", filters.batch);
  if (filters.sourceId) params.append("source_id", filters.sourceId);

  const response = await axiosInstance.get(
    `/search-training-leads?${params.toString()}`
  );

  if (!response.data || !response.data.results) {
    return [];
  }

  return response.data.results.map((item) => {
    const lead = item.training_lead || item;
    const address = item.address || {};
    const followups = item.followups || [];

    const latestFollowup =
      followups.length > 0 ? followups[followups.length - 1] : null;
    let nextFollowupDateStr = "—";
    if (latestFollowup && latestFollowup.Next_Followup_Date) {
      const date = new Date(latestFollowup.Next_Followup_Date);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        nextFollowupDateStr = `${day}/${month}/${year}`;
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return "—";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "—";
        return date.toLocaleDateString("en-GB");
      } catch {
        return "—";
      }
    };

    return {
      id: lead.Training_Lead_Id || lead.training_lead_id || null,
      name: `${lead.First_Name || lead.first_name || ""} ${
        lead.Last_Name || lead.last_name || ""
      }`.trim() || "—",
      email: lead.Email || lead.email || "—",
      phone:
        lead.Country_Code && (lead.Phone || lead.phone)
          ? `${lead.Country_Code}${lead.Phone || lead.phone}`
          : lead.Phone || lead.phone || "—",
      location: address.City_Name || address.city_name || "—",
      Courses: Array.isArray(lead.Courses) ? lead.Courses : [],
      Modes: Array.isArray(lead.Modes) ? lead.Modes : [],
      courseId: (() => {
        const arr = Array.isArray(lead.Courses) ? lead.Courses : [];
        const first = arr[0];
        return (
          (first && (first.Course_Id ?? first.course_id)) ||
          lead.Course_Id ||
          lead.course_id ||
          null
        );
      })(),
      status: lead.Status || lead.status || "—",
      source: lead.Source_Name || lead.source_name || "—",
      createdAt: formatDate(lead.Created_On || lead.created_on),
      lastFollowupDate: formatDate(lead.Updated_On || lead.updated_on),
      notes: latestFollowup?.Remarks || "—",
      nextFollowupDate: nextFollowupDateStr,
      isQuoteSent: "No",
    };
  });
}

/**
 * TrainingLeadsPage
 *
 * Encapsulates filter state, search logic, free search, and pagination
 * for the Training Leads tab. Receives initial `trainingLeads` from parent.
 */
function TrainingLeadsPage({
  trainingLeads = [],
  courses = [],
  rowsPerPage = 5,
  onViewLead,
  onEditLead,
}) {
  const [appliedFilters, setAppliedFilters] = useState({
    nameOrPhone: "",
    phone: "",
    city: "",
    courseId: "",
    status: "All Statuses",
    nextFollowupDate: "",
    mode: "",
    batch: "",
    sourceId: "",
    freeSearch: "",
  });
  const [filters, setFilters] = useState({
    nameOrPhone: "",
    phone: "",
    city: "",
    courseId: "",
    status: "All Statuses",
    nextFollowupDate: "",
    mode: "",
    batch: "",
    sourceId: "",
    freeSearch: "",
  });

  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const emptyFilters = {
    nameOrPhone: "",
    phone: "",
    city: "",
    courseId: "",
    status: "All Statuses",
    nextFollowupDate: "",
    mode: "",
    batch: "",
    sourceId: "",
    freeSearch: "",
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSearchResults(null);
    setIsSearching(false);
    setCurrentPage(1);
  };

  const hasSearchFilters = () => {
    return (
      filters.nameOrPhone ||
      filters.phone ||
      filters.city ||
      filters.courseId ||
      (filters.status && filters.status !== "All Statuses") ||
      filters.nextFollowupDate ||
      filters.mode ||
      filters.batch ||
      filters.sourceId
    );
  };

  const searchTrainingLeads = async () => {
    setIsSearching(true);
    try {
      const results = await searchTrainingLeadsApi(filters);
      setSearchResults(results);
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.message ||
        "Error searching training leads.";
      console.error("Error searching training leads:", msg);
      setSearchResults([]);
      alert(
        "Error searching training leads: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchClick = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
    if (hasSearchFilters()) {
      searchTrainingLeads();
    } else {
      setSearchResults(null);
      setIsSearching(false);
    }
  };

  const baseLeads = useMemo(
    () => (searchResults !== null ? searchResults : trainingLeads),
    [searchResults, trainingLeads]
  );

  const filteredLeads = useMemo(() => {
    const af = appliedFilters;
    return baseLeads.filter((lead) => {
      if (af.nameOrPhone && searchResults === null) {
        const searchValue = af.nameOrPhone.toLowerCase().trim();
        const nameMatch = lead.name?.toLowerCase().includes(searchValue);
        const phoneMatch = lead.phone?.includes(af.nameOrPhone.trim());
        if (!nameMatch && !phoneMatch) return false;
      }
      if (
        af.city &&
        !lead.location?.toLowerCase().includes(af.city.toLowerCase())
      ) {
        return false;
      }
      if (
        af.status &&
        af.status !== "All Statuses" &&
        String(lead.status || "")
          .trim()
          .toLowerCase() !== af.status.toLowerCase()
      ) {
        return false;
      }
      if (af.nextFollowupDate) {
        const filterDate = af.nextFollowupDate; // YYYY-MM-DD
        if (!lead.nextFollowupDate || lead.nextFollowupDate === "—") {
          return false;
        }
        const datePart = lead.nextFollowupDate.split(" ")[0] || "";
        let leadDateISO = "";
        if (datePart.includes("-") && datePart.length >= 10) {
          leadDateISO = datePart.substring(0, 10);
        } else {
          const [d, m, y] = datePart.split("/");
          if (y && m && d)
            leadDateISO = `${y}-${m.padStart(2, "0")}-${d.padStart(
              2,
              "0"
            )}`;
        }
        if (leadDateISO !== filterDate) return false;
      }
      if (af.courseId && lead.courseId) {
        if (String(lead.courseId) !== String(af.courseId)) return false;
      }
      return true;
    });
  }, [baseLeads, appliedFilters, searchResults]);

  const freeSearchFilteredLeads = useMemo(() => {
    if (!appliedFilters.freeSearch) return filteredLeads;
    const searchValue = appliedFilters.freeSearch.toLowerCase().trim();
    if (!searchValue) return filteredLeads;

    return filteredLeads.filter((lead) => {
      const name = (lead.name || "").toLowerCase();
      const email = (lead.email || "").toLowerCase();
      const phone = (lead.phone || "").toLowerCase();
      const location = (lead.location || "").toLowerCase();
      const createdAt = (lead.createdAt || "").toLowerCase();
      const lastFollowupDate = (lead.lastFollowupDate || "").toLowerCase();
      const notes = (lead.notes || "").toLowerCase();
      const nextFollowupDate = (lead.nextFollowupDate || "").toLowerCase();
      const status = (lead.status || "").toLowerCase();
      const source = (lead.source || "").toLowerCase();
      const isQuoteSent = (lead.isQuoteSent || "")
        .toString()
        .toLowerCase();

      return (
        name.includes(searchValue) ||
        email.includes(searchValue) ||
        phone.includes(searchValue) ||
        location.includes(searchValue) ||
        createdAt.includes(searchValue) ||
        lastFollowupDate.includes(searchValue) ||
        notes.includes(searchValue) ||
        nextFollowupDate.includes(searchValue) ||
        status.includes(searchValue) ||
        source.includes(searchValue) ||
        isQuoteSent.includes(searchValue)
      );
    });
  }, [filteredLeads, appliedFilters.freeSearch]);

  const totalPages = Math.ceil(freeSearchFilteredLeads.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentLeads = freeSearchFilteredLeads.slice(startIndex, endIndex);
  const showPagination = freeSearchFilteredLeads.length >= rowsPerPage;
  const startRow = freeSearchFilteredLeads.length > 0 ? startIndex + 1 : 0;
  const endRow = Math.min(endIndex, freeSearchFilteredLeads.length);
  const totalRows = freeSearchFilteredLeads.length;

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, "...", totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  return (
    <div className="training-leads-page">
      <div className="search-filters-row">
        <input
          type="text"
          className="search-filter-input"
          placeholder="Search name or phone..."
          value={filters.nameOrPhone}
          onChange={(e) => handleFilterChange("nameOrPhone", e.target.value)}
        />
        <input
          type="text"
          className="search-filter-input"
          placeholder="Search phone..."
          value={filters.phone}
          onChange={(e) => handleFilterChange("phone", e.target.value)}
        />
        <select
          className="search-filter-input search-filter-select"
          value={filters.city || ""}
          onChange={(e) => handleFilterChange("city", e.target.value)}
        >
          <option value="">All Locations</option>
          {/* Locations derived from existing leads */}
          {Array.from(
            new Set(
              trainingLeads
                .map((l) => l.location)
                .filter(Boolean)
                .map((s) => String(s).trim())
            )
          )
            .sort()
            .map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
        </select>
        <select
          className="search-filter-input search-filter-select"
          value={filters.courseId || ""}
          onChange={(e) => handleFilterChange("courseId", e.target.value)}
        >
          <option value="">All Courses</option>
          {courses &&
            courses
              .filter((c) => c.Status === "Active")
              .map((course) => (
                <option key={course.Course_Id} value={course.Course_Id}>
                  {course.Course_Code} - {course.Course_Name}
                </option>
              ))}
        </select>
        <select
          className="search-filter-input search-filter-select"
          value={filters.status || ""}
          onChange={(e) => handleFilterChange("status", e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="New">New</option>
          <option value="Interested">Interested</option>
          <option value="Followup">Followup</option>
          <option value="Not Interested">Not Interested</option>
        </select>
        <button
          type="button"
          className="btn-search"
          onClick={handleSearchClick}
        >
          {isSearching ? "Searching..." : "Search"}
        </button>
        <button
          type="button"
          className="btn-clear"
          onClick={handleClearFilters}
        >
          Clear
        </button>
      </div>

      <div className="free-search-bar search-in-actions-row">
        <input
          type="text"
          className="free-search-input"
          placeholder="Search..."
          value={filters.freeSearch}
          onChange={(e) => handleFilterChange("freeSearch", e.target.value)}
          title="Search across all columns"
        />
      </div>

      <div className="table-wrapper">
        <div className="table-container">
          <table className="data-table leads-table-frozen">
            <tbody>
              <TrainingLeadsTable
                rows={currentLeads}
                courses={courses}
                domains={[]}
                getColumnWidth={() => undefined}
                getColumnMinWidth={() => undefined}
                handleView={onViewLead}
                handleEdit={onEditLead}
                handleDelete={() => {}}
              />
            </tbody>
          </table>
        </div>
      </div>

      {showPagination && (
        <div className="pagination">
          <button
            className="pagination-btn pagination-arrow"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            {"<"}
          </button>
          <div className="page-numbers">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  className="page-number ellipsis"
                >
                  ...
                </span>
              ) : (
                <span
                  key={page}
                  className={`page-number ${
                    currentPage === page ? "active" : ""
                  }`}
                  onClick={() => handlePageClick(page)}
                >
                  {page}
                </span>
              )
            )}
          </div>
          <button
            className="pagination-btn pagination-arrow"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            {">"}
          </button>
          {totalRows > 0 && (
            <div className="pagination-info">
              {startRow}-{endRow} of {totalRows} items
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TrainingLeadsPage;

